import React, { useEffect, useState } from "react";
import Sidebar from "../../../../component/includes/sidebar";
import {
  Product,
  CreateProductData,
  UpdateProductData,
  productService,
} from "../../../../services/product/product.service";
import {
  ProductCategory,
  productCategoryService,
} from "../../../../services/product/product-category.service";
import ArrayInput from "../../../../component/includes/product/Array";
import {
  ImagePlus,
  Pencil,
  PlusCircle,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";

const DashboardProduct = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<Partial<CreateProductData>>({
    name: "",
    latin_name: "",
    synonym: "",
    familia: "",
    part_used: "",
    method_of_reproduction: "",
    harvest_age: "",
    morphology: "",
    area_name: "",
    efficacy: "",
    research_results: "",
    description: "",
    price: 0,
    unit_type: "",
    product_category_id: 0,
    utilization: [],
    composition: [],
  });
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [previewImages, setPreviewImages] = useState<Array<{
    url: string;
    isNew: boolean;
    id?: number;
  }>>([]);

  const handleImagePreview = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      const newPreviews = files.map((file) => ({
        url: URL.createObjectURL(file),
        isNew: true
      }));
      setPreviewImages((prev) => [...prev, ...newPreviews]);
      setSelectedFiles(files);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const productsData = await productService.getAllProducts();
        const categoriesData =
          await productCategoryService.getProductCategories();

        if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
        } else if (categoriesData && typeof categoriesData === "object") {
          const categoryArrayKey = Object.keys(categoriesData).find((key) =>
            Array.isArray((categoriesData as any)[key])
          );
          if (categoryArrayKey) {
            setCategories((categoriesData as any)[categoryArrayKey]);
          }
        }

        setProducts(Array.isArray(productsData) ? productsData : []);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load data";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);



  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "price" || name === "product_category_id"
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      latin_name: "",
      synonym: "",
      familia: "",
      part_used: "",
      method_of_reproduction: "",
      harvest_age: "",
      morphology: "",
      area_name: "",
      efficacy: "",
      research_results: "",
      description: "",
      price: 0,
      unit_type: "",
      product_category_id: 0,
      utilization: [],
      composition: [],
    });
    setSelectedFiles([]);
    setPreviewImages([]);
    setEditingProduct(null);
  };

  const handleError = (err: any, action: string) => {
    let errorMessage = `Gagal ${action} produk.`;
    if (err?.error) {
      errorMessage = typeof err.error === 'string' ? err.error : JSON.stringify(err.error);
    } else if (err?.message && typeof err.message === 'string') {
      if (err.message.includes("Conflict")) {
        errorMessage = "Terjadi konflik dengan data produk yang sudah ada. Silakan coba lagi.";
      } else {
        errorMessage = err.message;
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
    }
    
    if (err?.details) {
      errorMessage += ` (${typeof err.details === 'string' ? err.details : JSON.stringify(err.details)})`;
    }
    
    setError(errorMessage);
    alert(errorMessage);
  };

  const handleCreateProduct = async () => {
    if (!formData.name?.trim()) {
      alert("Nama produk tidak boleh kosong.");
      return;
    }
    if (!formData.price || formData.price <= 0 || isNaN(formData.price)) {
      alert("Harga harus lebih dari 0.");
      return;
    }
    if (!formData.product_category_id || isNaN(formData.product_category_id)) {
      alert("Silakan pilih kategori produk.");
      return;
    }
    if (selectedFiles.length === 0) {
      alert("Silakan pilih setidaknya satu gambar.");
      return;
    }

    try {
      const createData: CreateProductData = {
        ...formData,
        images: selectedFiles,
        product_category_id: formData.product_category_id,
      };

      await productService.createProduct(createData);
      setIsModalOpen(false);
      resetForm();
      alert("Produk berhasil dibuat!");
      window.location.reload();
    } catch (err: any) {
      handleError(err, "membuat");
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    if (!formData.name?.trim()) {
      alert("Nama produk tidak boleh kosong.");
      return;
    }
    if (!formData.price || formData.price <= 0 || isNaN(formData.price)) {
      alert("Harga harus lebih dari 0.");
      return;
    }
    if (!formData.product_category_id || isNaN(formData.product_category_id)) {
      alert("Silakan pilih kategori produk.");
      return;
    }

    try {
      const updateData: UpdateProductData = {
        id: editingProduct.id,
        ...formData,
        product_category_id: formData.product_category_id || undefined,
      };

      if (selectedFiles.length > 0) {
        updateData.images = selectedFiles;
      }

      await productService.updateProduct(updateData);
      setIsModalOpen(false);
      resetForm();
      alert("Produk berhasil diperbarui!");
      window.location.reload();
    } catch (err: any) {
      handleError(err, "memperbarui");
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this product?"
    );
    if (confirmDelete) {
      try {
        await productService.deleteProduct(productId);
        window.location.reload();
      } catch (err) {
        handleError(err, "delete");
        window.location.reload();
      }
    }
  };

  useEffect(() => {
    return () => {
      previewImages.forEach((preview) => {
        if (preview.isNew) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [previewImages]);

  const ensureArray = (value: any): string[] =>
    Array.isArray(value) ? value : [];

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setPreviewImages([]);

    if (product.images && product.images.length > 0) {
      const previews = product.images.map((image) => ({
        url: `${process.env.REACT_APP_API_URL}${image.image}`,
        isNew: false,
        id: image.id
      }));
      setPreviewImages(previews);
    }

    setFormData({
      name: product.name,
      latin_name: product.latin_name,
      synonym: product.synonym,
      familia: product.familia,
      part_used: product.part_used,
      method_of_reproduction: product.method_of_reproduction,
      harvest_age: product.harvest_age,
      morphology: product.morphology,
      area_name: product.area_name,
      efficacy: product.efficacy,
      research_results: product.research_results,
      description: product.description,
      price: product.price,
      unit_type: product.unit_type,
      product_category_id: product.product_category_id,
      utilization: ensureArray(product.utilization?.values || product.utilization),
      composition: ensureArray(product.composition?.values || product.composition),
    });

    setIsModalOpen(true);
  };

  const handleDeleteImage = async (index: number, imageId?: number) => {
    try {
      if (imageId && editingProduct) {
        await productService.deleteProductGalleryImages(
          parseInt(editingProduct.id),
          [imageId]
        );
      }

      setPreviewImages((prev) => prev.filter((_, i) => i !== index));
      
      if (!imageId) {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
      }
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-500" />
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        {error}
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="flex flex-col md:flex-row">
        <Sidebar />
        <div className="flex-1 p-4 md:p-6 mt-10">
          <div className="max-w-7xl mx-auto">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800">
                Daftar Produk
              </h1>
              <button
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition transform hover:scale-105 shadow-md"
              >
                <PlusCircle size={20} />
                <span>Tambah Produk</span>
              </button>
            </div>

            {/* Search Input */}
            <div className="relative mb-6 w-full sm:w-96">
              <input
                type="text"
                placeholder="Cari produk..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              />
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
            </div>

            {/* Product Table */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nama Produk
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nama Latin
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Harga
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Aksi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProducts.map((product, index) => (
                      <tr key={product.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {product.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 italic">
                          {product.latin_name || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          Rp. {Number(product.price).toLocaleString('id-ID')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-3">
                            <button
                              onClick={() => openEditModal(product)}
                              className="text-indigo-600 hover:text-indigo-900 transition"
                              title="Edit"
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id)}
                              className="text-red-600 hover:text-red-900 transition"
                              title="Hapus"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredProducts.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                          Tidak ada produk ditemukan
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Form - Updated Layout */}
            {isModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-all duration-300">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
                  <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h2 className="text-2xl font-bold text-gray-800">
                      {editingProduct ? "Edit Produk" : "Tambah Produk Baru"}
                    </h2>
                    <button
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <X size={24} />
                    </button>
                  </div>
                  <div className="p-8 overflow-y-auto">

                  {/* Informasi Dasar Section */}
                  <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-100 flex items-center gap-2">
                      Informasi Dasar
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Nama Produk
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="Masukkan nama produk"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Nama Latin
                        </label>
                        <input
                          type="text"
                          name="latin_name"
                          value={formData.latin_name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="Nama latin produk"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Gambar Produk
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:bg-gray-50 hover:border-green-500 transition-all cursor-pointer relative group">
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleImagePreview}
                            className="hidden"
                            id="file-upload"
                          />
                          <label
                            htmlFor="file-upload"
                            className="flex flex-col items-center justify-center cursor-pointer"
                          >
                            <ImagePlus size={48} className="text-gray-400 mb-2" />
                            <p className="text-gray-600">Unggah Gambar</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Maksimal 10 gambar
                            </p>
                          </label>
                          {previewImages.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-4">
                              {previewImages.map((preview, index) => (
                                <div key={index} className="relative">
                                  <img
                                    src={preview.url}
                                    alt={`Preview ${index + 1}`}
                                    className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                                  />
                                  <button
                                    onClick={() =>
                                      handleDeleteImage(index, preview.id)
                                    }
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Karakteristik Tanaman Section */}
                  <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-100 flex items-center gap-2">
                      Karakteristik Tanaman
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Sinonim
                        </label>
                        <input
                          type="text"
                          name="synonym"
                          value={formData.synonym}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="Sinonim tanaman"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Familia
                        </label>
                        <input
                          type="text"
                          name="familia"
                          value={formData.familia}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="Familia tanaman"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Bagian yang digunakan
                        </label>
                        <input
                          type="text"
                          name="part_used"
                          value={formData.part_used}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="Bagian yang digunakan"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Cara Perkembangbiakan
                        </label>
                        <input
                          type="text"
                          name="method_of_reproduction"
                          value={formData.method_of_reproduction}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="Cara perkembangbiakan"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Usia Panen
                        </label>
                        <input
                          type="text"
                          name="harvest_age"
                          value={formData.harvest_age}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="Usia panen"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Nama Daerah
                        </label>
                        <input
                          type="text"
                          name="area_name"
                          value={formData.area_name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="Nama daerah"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Morfologi Section */}
                  <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-100 flex items-center gap-2">
                      Morfologi
                    </h3>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Deskripsi Morfologi
                      </label>
                      <textarea
                        name="morphology"
                        value={formData.morphology}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                        rows={4}
                        placeholder="Deskripsi morfologi tanaman"
                      />
                    </div>
                  </div>

                  {/* Tags Produk / Pemanfaatan & Komposisi Section */}
                  <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-100 flex items-center gap-2">
                      Tags Produk
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <ArrayInput
                        label="Pemanfaatan"
                        value={formData.utilization}
                        onChange={(newValue) =>
                          setFormData((prev) => ({
                            ...prev,
                            utilization: newValue,
                          }))
                        }
                        placeholder="Tambah pemanfaatan"
                      />
                      <ArrayInput
                        label="Komposisi"
                        value={formData.composition}
                        onChange={(newValue) =>
                          setFormData((prev) => ({
                            ...prev,
                            composition: newValue,
                          }))
                        }
                        placeholder="Tambah komposisi"
                      />
                    </div>
                  </div>

                  {/* Hasil Riset Section */}
                  <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-100 flex items-center gap-2">
                      Hasil Riset
                    </h3>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Hasil Riset
                      </label>
                      <textarea
                        name="research_results"
                        value={formData.research_results}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                        rows={3}
                        placeholder="Hasil penelitian"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Deskripsi
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                        rows={3}
                        placeholder="Deskripsi lengkap produk"
                      />
                    </div>
                  </div>

                  {/* Harga & Kategori Section */}
                  <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-100 flex items-center gap-2">
                      Harga & Kategori
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Harga
                        </label>
                        <input
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Satuan
                        </label>
                        <input
                          type="text"
                          name="unit_type"
                          value={formData.unit_type}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                          placeholder="cth: kg, gram, pc"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Kategori
                        </label>
                        <select
                          name="product_category_id"
                          value={formData.product_category_id}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                        >
                          <option value="">Pilih Kategori</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Khasiat Section */}
                  <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-100 flex items-center gap-2">
                      Khasiat
                    </h3>
                    <textarea
                      name="efficacy"
                      value={formData.efficacy}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all shadow-sm"
                      rows={3}
                      placeholder="Khasiat tanaman"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                      className="px-6 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors font-medium"
                    >
                      Batal
                    </button>
                    <button
                      onClick={
                        editingProduct
                          ? handleUpdateProduct
                          : handleCreateProduct
                      }
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl flex items-center transition-colors shadow-lg shadow-green-200 font-medium"
                    >
                      <Upload size={16} className="mr-2" />
                      {editingProduct ? "Perbarui Produk" : "Simpan Produk"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardProduct;